import { useEffect, useMemo, useState } from "react";
import { RoleShell } from "@/components/RoleShell";
import {
  ShoppingBag, Clock, Package, BarChart3, FileText, Plus, X, Crown,
  Trash2, Search, Award, Barcode, Check as CheckIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import { useStaff } from "@/hooks/useCatalog";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { MemberBadge } from "@/components/MemberBadge";
import "@/components/membership.css";

const TABS = [
  { key: "log", label: "Sales Log", Icon: Clock },
  { key: "catalog", label: "Products Catalog", Icon: Package },
  { key: "perf", label: "Staff Performance", Icon: BarChart3 },
  { key: "reports", label: "Reports", Icon: FileText },
];

export default function ProductSales() {
  const [tab, setTab] = useState("log");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const { staff } = useStaff();
  const { activeBranchId } = useBranch();

  const reload = async () => {
    try {
      const [s, p, sl] = await Promise.all([
        api.get("/product-sales/stats"),
        api.get("/retail-products"),
        api.get("/product-sales"),
      ]);
      setStats(unwrap(s));
      setProducts(unwrap(p) || []);
      setSales(unwrap(sl) || []);
    } catch (e) {
      toast.error("Could not load sales");
    }
  };

  useEffect(() => {
    reload();
    const t = setInterval(reload, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <RoleShell title="Product Sales">
      <div className="max-w-[1700px] mx-auto px-6 pb-12 grid grid-cols-1 lg:grid-cols-[260px_1fr_340px] gap-5" data-testid="products-root">
        {/* Left sidebar */}
        <aside className="space-y-4">
          <div className="glass-primary rounded-[20px] p-4">
            <div className="overline mb-2">Today's sales</div>
            <div className="font-mono text-[28px] text-[var(--color-gold-deeper)] leading-none">
              ₹{Math.round(stats?.total_amount || 0).toLocaleString("en-IN")}
            </div>
            <div className="font-mono text-[13px] text-[var(--color-text-muted)] mt-1">
              {stats?.total_items || 0} items · {stats?.sales_count || 0} sales
            </div>
            <div className="font-mono text-[13px] text-[var(--color-success-deep)] mt-1">
              Commission: ₹{Math.round(stats?.total_commission || 0).toLocaleString("en-IN")}
            </div>
          </div>

          <div className="glass-primary rounded-[20px] p-2.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`ps-nav-pill ${tab === t.key ? "active" : ""}`}
                data-testid={`products-nav-${t.key}`}
              >
                <t.Icon size={15} /> {t.label}
              </button>
            ))}
          </div>

          {/* Leaderboard */}
          <div className="glass-primary rounded-[20px] p-4">
            <div className="overline mb-3 flex items-center gap-1.5">
              <Award size={11} /> Top sellers today
            </div>
            {(stats?.leaderboard || []).slice(0, 3).map((s, i) => (
              <div key={s.staff_id} className="flex items-center gap-3 py-2 border-b border-white/30 last:border-0">
                <span className="font-mono text-[16px] w-6 flex-shrink-0"
                      style={{ color: ["#D4A853", "#A8A29E", "#B5892E"][i] }}>#{i + 1}</span>
                <span className="font-sora font-medium text-[12.5px] truncate flex-1">{s.staff_name}</span>
                <span className="font-mono text-[12.5px] text-[var(--color-gold-deeper)]">₹{Math.round(s.revenue).toLocaleString("en-IN")}</span>
              </div>
            ))}
            {(!stats || (stats.leaderboard || []).length === 0) && (
              <div className="font-inter text-[12px] text-[var(--color-text-muted)] py-2">No sales yet today.</div>
            )}
          </div>
        </aside>

        {/* Center */}
        <section>
          {tab === "log" && <SalesLog sales={sales} />}
          {tab === "catalog" && <CatalogView products={products} onReload={reload} />}
          {tab === "perf" && <PerformanceView stats={stats} />}
          {tab === "reports" && <ReportsView stats={stats} products={products} />}
        </section>

        {/* Right: New Sale panel */}
        <aside>
          <NewSalePanel
            products={products}
            staff={staff}
            branchId={activeBranchId}
            onSaved={reload}
          />
        </aside>
      </div>
    </RoleShell>
  );
}

// ============================================================
// SALES LOG
// ============================================================
function SalesLog({ sales }) {
  return (
    <div data-testid="products-sales-log">
      <div className="overline mb-3">Recent sales</div>
      {sales.length === 0 && (
        <div className="glass-primary rounded-2xl p-10 text-center">
          <ShoppingBag className="mx-auto mb-3 text-[var(--color-text-muted)]" size={36} />
          <div className="font-sora text-[16px] font-semibold">No sales yet today.</div>
          <div className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">Record one from the panel on the right →</div>
        </div>
      )}
      <div className="space-y-2.5">
        {sales.map((s) => <SaleCard key={s.id} sale={s} />)}
      </div>
    </div>
  );
}

function SaleCard({ sale }) {
  return (
    <div className="glass-primary rounded-2xl p-4 flex items-center gap-4" data-testid={`sale-card-${sale.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-sora font-semibold text-[14px] text-[var(--color-text-primary)] truncate">
          {sale.primary_product_name}
          {sale.item_count > 1 && <span className="font-mono text-[12px] text-[var(--color-text-muted)]"> + {sale.item_count - 1} more</span>}
        </div>
        <div className="font-inter text-[12px] text-[var(--color-text-muted)] mt-0.5 truncate">
          by <span className="text-[var(--color-mauve-deep)]">{sale.sold_by_staff_name}</span> · to {sale.client_name || "Walk-in"} ·{" "}
          <span className="font-mono">{new Date(sale.sold_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
        <div className="font-inter text-[11.5px] mt-1">
          <span className="text-[var(--color-success-deep)] font-mono">+₹{Math.round(sale.total_margin)} margin</span>
          <span className="text-[var(--color-text-faint)] mx-2">·</span>
          <span className="text-[var(--color-purple)] font-mono">+₹{Math.round(sale.total_commission)} commission</span>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-mono text-[22px] text-[var(--color-gold-deeper)] leading-none">₹{Math.round(sale.total_amount)}</div>
        <div className="font-sora text-[10.5px] uppercase tracking-wider text-[var(--color-text-muted)] mt-1">{sale.payment_method}</div>
      </div>
    </div>
  );
}

// ============================================================
// CATALOG
// ============================================================
function CatalogView({ products, onReload }) {
  const [adding, setAdding] = useState(false);

  return (
    <div data-testid="products-catalog">
      <div className="flex items-center justify-between mb-4">
        <div className="overline">Products catalog · {products.length}</div>
        <button className="btn-3d btn-gold-3d btn-sm" onClick={() => setAdding(true)} data-testid="add-product-btn">
          <Plus size={14} /> Add Product
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {products.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
      {adding && <AddProductModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); onReload(); }} />}
    </div>
  );
}

function ProductCard({ product: p }) {
  const stockColor = p.stock_qty <= 0 ? "#B91C1C" : p.stock_qty <= p.reorder_level ? "#B45309" : "#15803D";
  const catEmoji = { haircare: "💇", skincare: "✨", nailcare: "💅", spa: "🧖" }[p.category] || "📦";
  return (
    <div className="glass-primary rounded-2xl p-4 flex flex-col gap-2" data-testid={`product-card-${p.id}`}>
      <div className="w-full h-20 rounded-xl flex items-center justify-center text-3xl"
           style={{ background: "linear-gradient(135deg,rgba(212,168,83,0.10),rgba(124,92,122,0.10))" }}>
        {catEmoji}
      </div>
      <div className="font-sora font-semibold text-[14px] leading-tight">{p.name}</div>
      <div className="flex items-center gap-2 text-[11.5px]">
        {p.brand && <span className="px-2 py-0.5 rounded-full bg-white/40 font-inter font-medium">{p.brand}</span>}
        <span className="px-2 py-0.5 rounded-full font-inter font-medium capitalize"
              style={{ background: "rgba(124,92,122,0.10)", color: "#5E4060" }}>{p.category}</span>
      </div>
      <div className="flex items-center justify-between mt-1">
        <div>
          <div className="font-mono text-[18px] text-[var(--color-gold-deeper)]">₹{p.retail_price}</div>
          <div className="font-mono text-[10.5px] text-[var(--color-text-muted)]">cost ₹{p.cost_price} · {p.margin_percent}%</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[13px]" style={{ color: stockColor }}>{p.stock_qty} in stock</div>
          <div className="font-mono text-[10.5px] text-[var(--color-purple)]">{p.commission_rate}% · ₹{p.commission_amount}</div>
        </div>
      </div>
    </div>
  );
}

function AddProductModal({ onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", brand: "", category: "haircare",
    retail_price: 0, cost_price: 0, stock_qty: 0, reorder_level: 5,
    commission_rate: 10,
  });
  const submit = async () => {
    if (!f.name || !f.retail_price) { toast.error("Name and retail price required"); return; }
    try {
      await api.post("/retail-products", f);
      toast.success("Product added");
      onSaved();
    } catch { toast.error("Failed to add"); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
         style={{ background: "rgba(28,25,23,0.55)" }}>
      <div className="glass-elevated rounded-2xl p-6 w-full max-w-[480px] anim-slide-up">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-sora font-bold text-[18px]">Add retail product</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>
        {[["Name", "name"], ["Brand", "brand"]].map(([l, k]) => (
          <Field key={k} label={l} value={f[k]} onChange={(v) => setF((p) => ({ ...p, [k]: v }))} />
        ))}
        <div className="mb-3">
          <label className="block font-inter text-[11.5px] text-[var(--color-text-muted)] mb-1">Category</label>
          <select value={f.category} onChange={(e) => setF((p) => ({ ...p, category: e.target.value }))}
                  className="w-full glass-subtle rounded-lg px-3 py-2 outline-none text-[13px]">
            <option value="haircare">Haircare</option>
            <option value="skincare">Skincare</option>
            <option value="nailcare">Nailcare</option>
            <option value="spa">Spa</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Retail price ₹" mono value={f.retail_price} onChange={(v) => setF((p) => ({ ...p, retail_price: parseFloat(v) || 0 }))} type="number" />
          <Field label="Cost price ₹" mono value={f.cost_price} onChange={(v) => setF((p) => ({ ...p, cost_price: parseFloat(v) || 0 }))} type="number" />
          <Field label="Stock qty" mono value={f.stock_qty} onChange={(v) => setF((p) => ({ ...p, stock_qty: parseFloat(v) || 0 }))} type="number" />
          <Field label="Reorder at" mono value={f.reorder_level} onChange={(v) => setF((p) => ({ ...p, reorder_level: parseFloat(v) || 0 }))} type="number" />
          <Field label="Commission %" mono value={f.commission_rate} onChange={(v) => setF((p) => ({ ...p, commission_rate: parseFloat(v) || 0 }))} type="number" />
        </div>
        <button onClick={submit} className="btn-3d btn-gold-3d btn-md w-full mt-4" data-testid="add-product-submit">Save Product</button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, mono, type = "text" }) {
  return (
    <div className="mb-3">
      <label className="block font-inter text-[11.5px] text-[var(--color-text-muted)] mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
             className={`w-full glass-subtle rounded-lg px-3 py-2 outline-none text-[13.5px] ${mono ? "font-mono" : "font-inter"}`} />
    </div>
  );
}

// ============================================================
// PERFORMANCE
// ============================================================
function PerformanceView({ stats }) {
  const board = stats?.leaderboard || [];
  const max = Math.max(...board.map((b) => b.revenue), 1);
  return (
    <div data-testid="products-performance">
      <div className="overline mb-3">Staff performance — today</div>
      <div className="glass-primary rounded-2xl p-5">
        {board.length === 0 && (
          <div className="text-center py-8 font-inter text-[13px] text-[var(--color-text-muted)]">No sales yet today.</div>
        )}
        {board.map((s) => (
          <div key={s.staff_id} className="mb-3 last:mb-0">
            <div className="flex items-center justify-between mb-1">
              <span className="font-sora font-semibold text-[13.5px]">{s.staff_name}</span>
              <span className="font-mono text-[14px] text-[var(--color-gold-deeper)]">₹{Math.round(s.revenue).toLocaleString("en-IN")}</span>
            </div>
            <div className="h-3 rounded-full bg-white/40 overflow-hidden">
              <div className="h-full rounded-full"
                   style={{ width: `${(s.revenue / max) * 100}%`,
                            background: "linear-gradient(90deg,#F0D898,#D4A853,#B5892E)" }} />
            </div>
            <div className="flex items-center gap-3 mt-1 font-mono text-[11px] text-[var(--color-text-muted)]">
              <span>{s.units_sold} units</span>
              <span>·</span>
              <span className="text-[var(--color-purple)]">₹{Math.round(s.commission_earned)} commission</span>
            </div>
          </div>
        ))}
      </div>

      {stats?.top_products?.length > 0 && (
        <>
          <div className="overline mt-6 mb-3">Top products</div>
          <div className="glass-primary rounded-2xl p-5">
            {stats.top_products.map((p) => (
              <div key={p.product_id} className="flex items-center justify-between py-2 border-b border-white/30 last:border-0">
                <span className="font-inter text-[13px] flex-1 truncate">{p.product_name}</span>
                <span className="font-mono text-[12.5px] text-[var(--color-text-muted)] mr-3">{p.units} sold</span>
                <span className="font-mono text-[13.5px] text-[var(--color-gold-deeper)]">₹{Math.round(p.revenue).toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// REPORTS
// ============================================================
function ReportsView({ stats, products }) {
  const low = products.filter((p) => p.stock_qty <= p.reorder_level);
  return (
    <div data-testid="products-reports">
      <div className="overline mb-3">Reports & alerts</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ReportCard title="Daily Sales Summary"
          stats={[
            ["Revenue", `₹${Math.round(stats?.total_amount || 0).toLocaleString("en-IN")}`],
            ["Items", stats?.total_items || 0],
            ["Margin", `₹${Math.round(stats?.total_margin || 0).toLocaleString("en-IN")}`],
            ["Commission", `₹${Math.round(stats?.total_commission || 0).toLocaleString("en-IN")}`],
          ]}
        />
        <ReportCard title="Low stock alert" badge={low.length}>
          {low.length === 0 && <div className="font-inter text-[13px] text-[var(--color-text-muted)]">All products well-stocked ✓</div>}
          {low.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-1.5 border-b border-white/30 last:border-0">
              <span className="font-inter text-[12.5px]">{p.name}</span>
              <span className="font-mono text-[12px]" style={{ color: p.stock_qty <= 0 ? "#B91C1C" : "#B45309" }}>
                {p.stock_qty} / {p.reorder_level}
              </span>
            </div>
          ))}
        </ReportCard>
      </div>
    </div>
  );
}

function ReportCard({ title, stats, children, badge }) {
  return (
    <div className="glass-primary rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-sora font-semibold text-[14px]">{title}</h3>
        {badge != null && (
          <span className="px-2 py-0.5 rounded-full font-mono text-[11px]"
                style={{ background: "rgba(245,158,11,0.14)", color: "#B45309" }}>{badge}</span>
        )}
      </div>
      {stats && stats.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between py-1 text-[13px]">
          <span className="font-inter text-[var(--color-text-muted)]">{k}</span>
          <span className="font-mono">{v}</span>
        </div>
      ))}
      {children}
    </div>
  );
}

// ============================================================
// NEW SALE PANEL (right column)
// ============================================================
function NewSalePanel({ products, staff, branchId, onSaved }) {
  const [query, setQuery] = useState("");
  const [skuMode, setSkuMode] = useState(false);
  const [skuFeedback, setSkuFeedback] = useState(null);   // { ok: bool, text: str }
  const [cart, setCart] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [clientPhoneQuery, setClientPhoneQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [client, setClient] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [submitting, setSubmitting] = useState(false);

  const filtered = useMemo(() => {
    if (skuMode || !query) return [];
    return products.filter((p) =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      (p.brand || "").toLowerCase().includes(query.toLowerCase())
    ).slice(0, 5);
  }, [query, products, skuMode]);

  useEffect(() => {
    const digits = clientPhoneQuery.replace(/\D/g, "");
    if (digits.length < 4) { setClientResults([]); return; }
    const t = setTimeout(() => {
      api.get("/clients/search", { params: { phone: digits } })
        .then((r) => setClientResults(unwrap(r) || []))
        .catch(() => {});
    }, 280);
    return () => clearTimeout(t);
  }, [clientPhoneQuery]);

  // Default staff (auto-select first stylist if any)
  useEffect(() => {
    if (staffId || staff.length === 0) return;
    const first = staff.find((s) => s.role === "stylist") || staff[0];
    if (first) setStaffId(first.id);
  }, [staff, staffId]);

  const total = cart.reduce((s, c) => s + c.product.retail_price * c.qty, 0);
  const commission = cart.reduce((s, c) =>
    s + c.product.retail_price * c.qty * (c.product.commission_rate / 100), 0);
  const memberDiscountPct = client?.membership?.discount_percent || 0;
  const memberDiscount = memberDiscountPct ? Math.round(total * memberDiscountPct / 100) : 0;
  const finalTotal = total - memberDiscount;

  const addProduct = (p) => {
    setCart((c) => {
      const ex = c.find((x) => x.product.id === p.id);
      if (ex) return c.map((x) => x.product.id === p.id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { product: p, qty: 1 }];
    });
    setQuery("");
  };

  const lookupSku = async () => {
    const sku = query.trim();
    if (!sku) return;
    try {
      const r = await api.get("/retail-products", { params: { sku } });
      const matches = unwrap(r) || [];
      if (matches.length === 0) {
        setSkuFeedback({ ok: false, text: `SKU "${sku}" not found — try search by name` });
        return;
      }
      const p = matches[0];
      addProduct(p);
      setSkuFeedback({ ok: true, text: `${p.name} added` });
      setQuery("");
      setTimeout(() => setSkuFeedback(null), 1800);
    } catch {
      setSkuFeedback({ ok: false, text: "Lookup failed" });
    }
  };

  const handleSearchKey = (e) => {
    if (skuMode && e.key === "Enter") {
      e.preventDefault();
      lookupSku();
    }
  };

  const submit = async () => {
    if (cart.length === 0) { toast.error("Add at least one product"); return; }
    if (!staffId) { toast.error("Select selling staff"); return; }
    setSubmitting(true);
    try {
      await api.post("/product-sales", {
        items: cart.map((c) => ({ product_id: c.product.id, quantity: c.qty })),
        sold_by_staff_id: staffId,
        client_id: client?.id || null,
        client_name: client?.name || null,
        client_phone: client?.phone || null,
        payment_method: paymentMethod,
        branch_id: branchId,
      });
      // Burst animation via toast + custom confetti
      const $el = document.querySelector("[data-testid='new-sale-btn']");
      spawnGoldBurst($el);
      toast.success(`Sale recorded · ₹${finalTotal} · Commission ₹${Math.round(commission)}`);
      // Reset
      setCart([]); setClient(null); setClientPhoneQuery(""); setQuery("");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Sale failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="glass-elevated rounded-[20px] p-5 sticky top-[88px]" data-testid="new-sale-panel">
      <div className="overline mb-3 flex items-center gap-1.5">
        <ShoppingBag size={11} /> Record a sale
      </div>

      {/* Products search */}
      <div className="relative">
        <div className={`flex items-center h-11 glass-subtle rounded-xl px-3.5 gap-2 ${skuMode ? "ring-2 ring-[var(--color-gold-mid)]/40" : ""}`}>
          {skuMode ? <Barcode size={14} className="text-[var(--color-gold-deeper)]" /> : <Search size={14} className="text-[var(--color-text-muted)]" />}
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleSearchKey}
            placeholder={skuMode ? "Scan or type SKU (e.g. LP-001) + Enter" : "Search product name or brand…"}
            className={`flex-1 bg-transparent outline-none text-[13px] ${skuMode ? "font-mono uppercase tracking-wider" : "font-inter"}`}
            data-testid="new-sale-product-search"
          />
          <button
            type="button"
            onClick={() => { setSkuMode(!skuMode); setQuery(""); setSkuFeedback(null); }}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition ${skuMode
              ? "bg-[var(--color-gold-mid)] text-white"
              : "glass-subtle text-[var(--color-text-muted)] hover:text-[var(--color-gold-deeper)]"
            }`}
            aria-label={skuMode ? "Switch to search mode" : "Switch to SKU mode"}
            title={skuMode ? "Switch to search mode" : "Scan / enter SKU"}
            data-testid="new-sale-sku-toggle"
          >
            <Barcode size={13} />
          </button>
        </div>
        {skuMode && skuFeedback && (
          <div
            className={`mt-1.5 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 text-[11.5px] font-inter font-medium ${skuFeedback.ok ? "" : ""}`}
            style={skuFeedback.ok
              ? { background: "rgba(34,197,94,0.14)", color: "#15803D" }
              : { background: "rgba(185,28,28,0.10)", color: "#991B1B" }}
            data-testid="new-sale-sku-feedback"
          >
            {skuFeedback.ok ? <CheckIcon size={12} /> : <X size={12} />} {skuFeedback.text}
          </div>
        )}
        {filtered.length > 0 && (
          <div className="absolute left-0 right-0 mt-1 glass-elevated rounded-xl py-1 z-30 max-h-60 overflow-y-auto">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => addProduct(p)}
                      className="w-full flex justify-between items-center px-3 py-2 text-left hover:bg-white/40">
                <div>
                  <div className="font-inter text-[12.5px] font-medium">{p.name}</div>
                  <div className="font-mono text-[10.5px] text-[var(--color-text-muted)]">{p.brand} · stock {p.stock_qty}</div>
                </div>
                <div className="font-mono text-[13px] text-[var(--color-gold-deeper)]">₹{p.retail_price}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Cart */}
      {cart.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {cart.map((c) => (
            <div key={c.product.id} className="flex items-center gap-2 glass-subtle rounded-lg px-2.5 py-2">
              <div className="flex-1 min-w-0">
                <div className="font-inter text-[12.5px] truncate">{c.product.name}</div>
                <div className="font-mono text-[10.5px] text-[var(--color-text-muted)]">₹{c.product.retail_price} × {c.qty}</div>
              </div>
              <button onClick={() => setCart((cc) => cc.map((x) => x.product.id === c.product.id ? { ...x, qty: Math.max(1, x.qty - 1) } : x))}
                      className="w-7 h-7 rounded-full glass-subtle font-bold text-[12px]">-</button>
              <span className="font-mono text-[13px] w-5 text-center">{c.qty}</span>
              <button onClick={() => setCart((cc) => cc.map((x) => x.product.id === c.product.id ? { ...x, qty: x.qty + 1 } : x))}
                      className="w-7 h-7 rounded-full glass-subtle font-bold text-[12px]">+</button>
              <button onClick={() => setCart((cc) => cc.filter((x) => x.product.id !== c.product.id))}
                      className="w-7 h-7 rounded-full text-[var(--color-danger)]"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Staff */}
      <div className="mt-4">
        <label className="block font-inter text-[11.5px] text-[var(--color-text-muted)] mb-1">Selling staff</label>
        <select value={staffId} onChange={(e) => setStaffId(e.target.value)}
                className="w-full glass-subtle rounded-lg px-3 py-2 outline-none text-[13px]"
                data-testid="new-sale-staff">
          <option value="">— select —</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.role}</option>)}
        </select>
        {staffId && commission > 0 && (
          <div className="font-mono text-[11.5px] text-[var(--color-purple)] mt-1">
            {staff.find((x) => x.id === staffId)?.name?.split(" ")[0]} earns ₹{Math.round(commission)} on this sale
          </div>
        )}
      </div>

      {/* Client */}
      <div className="mt-3">
        <label className="block font-inter text-[11.5px] text-[var(--color-text-muted)] mb-1">Client (optional)</label>
        {client ? (
          <div className="flex items-center justify-between glass-subtle rounded-lg px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-sora font-medium text-[13px] truncate">{client.name}</span>
              {client.membership && <MemberBadge membership={client.membership} />}
            </div>
            <button onClick={() => { setClient(null); setClientPhoneQuery(""); }} className="text-[var(--color-text-muted)]"><X size={14} /></button>
          </div>
        ) : (
          <input value={clientPhoneQuery} onChange={(e) => setClientPhoneQuery(e.target.value)}
                 placeholder="Phone digits (or skip for walk-in)"
                 className="w-full glass-subtle rounded-lg px-3 py-2 outline-none font-mono text-[13px]"
                 data-testid="new-sale-client-phone" />
        )}
        {!client && clientResults.length > 0 && (
          <div className="mt-1 glass-elevated rounded-lg py-1 max-h-40 overflow-y-auto">
            {clientResults.map((c) => (
              <button key={c.id} onClick={() => setClient(c)}
                      className="w-full text-left px-3 py-1.5 hover:bg-white/40 flex items-center gap-2">
                <span className="font-inter text-[12.5px]">{c.name}</span>
                {c.membership && <MemberBadge membership={c.membership} />}
                <span className="font-mono text-[10.5px] text-[var(--color-text-muted)] ml-auto">{c.phone}</span>
              </button>
            ))}
          </div>
        )}
        {memberDiscount > 0 && (
          <div className="mt-1.5 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5"
               style={{ background: "rgba(212,168,83,0.14)", color: "#8E6A1A" }}>
            <Crown size={12} /> <span className="font-inter text-[11.5px] font-medium">{client.membership.badge_label} member — {memberDiscountPct}% off: -₹{memberDiscount}</span>
          </div>
        )}
      </div>

      {/* Payment */}
      <div className="mt-3 flex gap-1.5">
        {["cash", "upi", "card"].map((m) => (
          <button key={m} onClick={() => setPaymentMethod(m)}
                  className={`flex-1 h-9 rounded-full font-sora font-semibold text-[11.5px] uppercase tracking-wider ${paymentMethod === m
                    ? "bg-[var(--color-mauve)] text-white"
                    : "glass-subtle text-[var(--color-text-secondary)]"
                  }`}>
            {m}
          </button>
        ))}
      </div>

      {/* Total + submit */}
      <div className="flex items-baseline justify-between mt-4">
        <span className="font-inter text-[12px] text-[var(--color-text-muted)]">Total</span>
        <span className="font-mono text-[28px] text-[var(--color-gold-deeper)]">₹{Math.max(0, finalTotal).toLocaleString("en-IN")}</span>
      </div>

      <button onClick={submit} disabled={submitting || cart.length === 0}
              className="btn-3d btn-gold-3d btn-xl w-full mt-3 relative"
              data-testid="new-sale-btn">
        <span className="particle-burst" id="new-sale-burst" />
        {submitting ? "Recording…" : "Complete Sale"}
      </button>
    </div>
  );
}

// Pure-CSS gold burst on submit button
function spawnGoldBurst(target) {
  if (!target) return;
  const burst = target.querySelector(".particle-burst");
  if (!burst) return;
  burst.innerHTML = "";
  for (let i = 0; i < 12; i++) {
    const el = document.createElement("span");
    el.className = "particle";
    el.style.setProperty("--a", `${(i / 12) * 360}deg`);
    burst.appendChild(el);
  }
  setTimeout(() => { burst.innerHTML = ""; }, 1000);
}
